import React from 'react';
import { useTranslation } from 'i18n';
import { Button, Divider, Grid, Link, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import domPurify from 'utils/domPurify';

import { getIconByVirtualPlatform } from 'components/common/iconsByVirtualPlatforms';
import IconWithText from 'components/common/IconWithText';

interface HowToParticipateProps {
  howToParticipate: string;
  livestream_link?: string;
  virtualPlatform?: 'custom' | 'zoom' | 'facebook_live' | 'youtube';
}

const useStyles = makeStyles()((theme) => ({
  container: {
    padding: theme.spacing(4),
    borderRadius: 8,
    background: theme.palette.background.default,
  },
  description: {
    wordBreak: 'break-word',
    lineHeight: '21px',
    fontWeight: 500,
  },
  icon: {
    width: '100px',
  },
  link: {
    wordBreak: 'break-word',
    textDecoration: 'none',
    fontWeight: 500,
    fontSize: 13,
  },
}));

const HowToParticipate: React.FC<React.PropsWithChildren<HowToParticipateProps>> = ({
  howToParticipate,
  virtualPlatform,
  livestream_link,
}) => {
  const { classes } = useStyles();
  const [t] = useTranslation('public');

  return (
    <Grid container direction="column" sx={{ p: 2 }} className={classes.container}>
      {virtualPlatform === 'custom' ? (
        <Grid item>
          <IconWithText
            icon={getIconByVirtualPlatform(virtualPlatform)}
            text={t<string>('event.eventSummary.onlineEvent')}
          />
        </Grid>
      ) : (
        <Grid item className={classes.icon}>
          {getIconByVirtualPlatform(virtualPlatform)}
        </Grid>
      )}
      {livestream_link && (
        <Grid item xs={12}>
          <Grid container>
            <Grid item xs={12} sx={{ pb: 4, pt: 4 }}>
              <Button size="small" variant="contained" color="secondary" href={livestream_link} target="_blank">
                {t<string>('common:button.joinSession')}
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
          </Grid>
        </Grid>
      )}
      {howToParticipate && (
        <Grid item>
          <Typography
            variant="body2"
            component="div"
            color="textSecondary"
            className={classes.description}
            dangerouslySetInnerHTML={{
              __html: domPurify.sanitize(howToParticipate),
            }}
          />
        </Grid>
      )}
      {livestream_link && (
        <Grid item>
          <Link className={classes.link} href={livestream_link} target="_blank">
            {livestream_link}
          </Link>
        </Grid>
      )}
    </Grid>
  );
};

export default HowToParticipate;
